/**
 * Road Defects Inventory Config
 *
 * @module roadways/config
 * @description Configuración de inventario para defectos viales
 */

import type { InventoryPanelConfig, InventoryRow } from '../../../layouts/panels/LeftPanel';
import { useRoadDefects } from '../hooks/useRoadDefects';
import { useRoadwaysElementColorsStore } from '../store/element-colors-store';
import type { RoadDefectReport } from '../types/road-defect.types';

/**
 * Configuración del panel de inventario de defectos viales
 */
export const roadDefectsInventoryConfig: InventoryPanelConfig<RoadDefectReport> = {
  entityType: 'road-defect',
  displayName: 'Defectos Viales',
  displayNameSingular: 'defecto vial',

  narrowColumns: [
    { key: 'code', label: 'Código', title: 'Código del reporte', width: '70px' },
    { key: 'name', label: 'Nombre', title: 'Descripción del defecto', width: '1fr' },
  ],

  wideColumns: [
    { key: 'code', label: 'Cód', title: 'Código del reporte', width: '70px' },
    { key: 'name', label: 'Descripción', title: 'Descripción del defecto', width: '1.5fr' },
    { key: 'subtype', label: 'Tipo', title: 'Tipo de defecto', width: '100px' },
    { key: 'severity', label: 'Severidad', title: 'Nivel de severidad', width: '80px' },
    { key: 'status', label: 'Estado', title: 'Estado del reporte', width: '90px' },
    { key: 'reported_at', label: 'Reportado', title: 'Fecha de reporte', width: '90px' },
  ],

  useData: (projectId: string) => {
    const {
      roadDefects,
      isLoading,
      error,
      loadRoadDefects,
      deleteRoadDefect,
    } = useRoadDefects({ projectId, autoLoad: true });

    return {
      data: roadDefects,
      isLoading,
      error,
      reload: loadRoadDefects,
      delete: deleteRoadDefect,
    };
  },

  buildRow: (defect: RoadDefectReport): InventoryRow => ({
    id: defect.id,
    code: defect.code,
    name: defect.name,
    subtype: defect.subtype,
    severity: defect.severity,
    status: defect.status,
    reported_at: new Date(defect.reported_at).toLocaleDateString('es-AR'),
  }),

  events: {
    select: 'roadways-defect-select',
    visibilityToggle: 'roadways-defect-visibility-toggle',
    hover: 'roadways-defect-hover',
    zoom: 'roadways-defect-zoom',
    created: 'road-defect-created',
    updated: 'road-defect-updated',
    deleted: 'road-defect-deleted',
  },

  getZoomTarget: (defect) => ({
    type: 'point',
    coordinates: defect.geometry.coordinates,
  }),

  colorStore: {
    getColor: (key) => useRoadwaysElementColorsStore.getState().getColor(key),
    setColor: (key, hex) => useRoadwaysElementColorsStore.getState().setColor(key, hex),
    colorKey: 'road-defects',
  },

  moduleId: 'roadways',
};
